import { useRef, useState, type FormEvent } from 'react'
import { formatWhen, statusExpiryPresets } from '../../lib/schedule'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ApiError, type User } from '../../api/client'
import { Avatar } from '../../components/avatar'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { meQuery } from '../../lib/auth'

// DIS-32: Settings › Profile — display name, handle, status, avatar.
export const Route = createFileRoute('/_app/settings/profile')({
  component: ProfilePage,
})

function ProfilePage() {
  const { me: ctxMe } = Route.useRouteContext()
  // Live copy: DND / status saves patch the cache and the page must follow.
  const me = useQuery({ ...meQuery, initialData: ctxMe }).data ?? ctxMe
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)

  const setMe = (u: User) => qc.setQueryData(meQuery.queryKey, u)

  const save = useMutation({
    mutationFn: async (body: { display_name: string; handle: string; status_text: string; status_emoji: string; status_clears_at?: string | null }) => {
      const { data, error: err, response } = await api.PATCH('/me', { body })
      if (!data) throw Object.assign(new Error((err as ApiError | undefined)?.message ?? `failed (${response.status})`), { api: err })
      return data
    },
    onSuccess: (u) => {
      setMe(u)
      setSaved(true)
      setError(null)
      setFields({})
    },
    onError: (e: Error & { api?: ApiError }) => {
      setSaved(false)
      setFields(e.api?.fields ?? {})
      setError(e.message)
    },
  })

  const dnd = useMutation({
    mutationFn: async (minutes: number) => {
      const until = minutes > 0 ? new Date(Date.now() + minutes * 60_000).toISOString() : null
      const { data, error: err, response } = await api.PUT('/me/dnd', { body: { until } })
      if (!data) throw new Error((err as ApiError | undefined)?.message ?? `failed (${response.status})`)
      return data
    },
    onSuccess: (u) => {
      setMe(u)
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })
  const [openedAt] = useState(() => Date.now())
  const dndActive = !!me.dnd_until && new Date(me.dnd_until).getTime() > openedAt

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      // openapi-fetch would JSON-encode; use fetch directly for multipart.
      const res = await fetch('/api/me/avatar', { method: 'POST', body: fd, credentials: 'same-origin' })
      const json = (await res.json()) as User | ApiError
      if (!res.ok) throw new Error((json as ApiError).fields?.file ?? (json as ApiError).message ?? `upload failed (${res.status})`)
      return json as User
    },
    onSuccess: (u) => {
      setMe(u)
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: async () => {
      const { data, response } = await api.DELETE('/me/avatar', {})
      if (!data) throw new Error(`remove failed (${response.status})`)
      return data
    },
    onSuccess: (u) => setMe(u),
    onError: (e: Error) => setError(e.message),
  })

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    save.mutate({
      display_name: String(f.get('display_name') ?? ''),
      handle: String(f.get('handle') ?? '').trim().toLowerCase(),
      status_text: String(f.get('status_text') ?? ''),
      status_emoji: String(f.get('status_emoji') ?? ''),
      status_clears_at: (() => {
        const v = String(f.get('status_clears') ?? 'never')
        if (v === 'keep') return me.status_clears_at ?? null
        const p = statusExpiryPresets().find((x) => x.value === v)
        return p?.at ? p.at().toISOString() : null
      })(),
    })
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8 p-6">
      <section className="space-y-3">
        <h1 className="text-lg font-semibold">Profile</h1>
        <div className="flex items-center gap-4 rounded-lg border border-border bg-bg-muted p-4">
          <Avatar user={me} size={72} />
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={upload.isPending} onClick={() => fileRef.current?.click()}>
                {upload.isPending ? 'Uploading…' : 'Upload photo'}
              </Button>
              {me.avatar_url && (
                <Button variant="ghost" size="sm" disabled={remove.isPending} onClick={() => remove.mutate()}>
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-fg-muted">JPEG, PNG, GIF or WebP up to 5 MB. Cropped to a square and resized to 256 px.</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0]
                if (f) upload.mutate(f)
                e.currentTarget.value = ''
              }}
            />
          </div>
        </div>
      </section>

      <form onSubmit={submit} className="space-y-4 rounded-lg border border-border bg-bg-muted p-4">
        <Field label="Display name" error={fields.display_name}>
          <Input name="display_name" defaultValue={me.display_name} required maxLength={80} />
        </Field>
        <Field label="Handle" error={fields.handle} hint="Used for @mentions. Lowercase letters, numbers, _ . -">
          <div className="flex items-center gap-1">
            <span className="text-fg-muted">@</span>
            <Input name="handle" defaultValue={me.handle} required pattern="[a-zA-Z0-9_.\-]{2,32}" />
          </div>
        </Field>
        <div className="grid grid-cols-[80px_1fr] gap-3">
          <Field label="Emoji">
            <Input name="status_emoji" defaultValue={me.status_emoji ?? ''} maxLength={32} placeholder="🛠️" />
          </Field>
          <Field label="Status">
            <Input name="status_text" defaultValue={me.status_text ?? ''} maxLength={120} placeholder="What are you up to?" />
          </Field>
        </div>
        <Field label="Clear status" hint={me.status_clears_at && new Date(me.status_clears_at).getTime() > openedAt ? `Clears ${formatWhen(me.status_clears_at)}` : 'Your status stays until you change it.'}>
          <select name="status_clears" aria-label="Clear status" defaultValue={me.status_clears_at ? 'keep' : 'never'} className="h-9 rounded-md border border-border bg-bg px-2 text-sm">
            {me.status_clears_at && <option value="keep">Keep current expiry</option>}
            {statusExpiryPresets().map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Do not disturb" hint={dndActive ? `On until ${new Date(me.dnd_until!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Pauses notifications and shows you as busy.'}>
          <select
            aria-label="Do not disturb"
            value={dndActive ? 'on' : 'off'}
            onChange={(e) => dnd.mutate(e.target.value === 'off' ? 0 : Number(e.target.value))}
            className="h-9 rounded-md border border-border bg-bg px-2 text-sm"
          >
            <option value="off">Off</option>
            {dndActive && <option value="on">On</option>}
            <option value="30">For 30 minutes</option>
            <option value="60">For 1 hour</option>
            <option value="240">For 4 hours</option>
            <option value="1440">For 24 hours</option>
          </select>
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {saved && <span className="text-sm text-fg-muted">Saved.</span>}
        </div>
      </form>
    </div>
  )
}

function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {error ? (
        <span className="block text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-fg-muted">{hint}</span>
      ) : null}
    </label>
  )
}
